// App.js – Realtime "phone call" UI powered by Cartesia Agent over LiveKit
// ---------------------------------------------------------------------------
// ✅ One BIG button: Login → Join Call → Leave Call
// ✅ WebRTC: publish mic, auto-play agent audio (barge-in handled by the agent)
// ✅ Keyboard & screen-reader friendly (WCAG-oriented)
// ✅ Optional captions via LiveKit data channel
// ✅ CASA-compliant session management with proper logout
// ✅ Fixed endpoint configuration for dual backend architecture
// ---------------------------------------------------------------------------

import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert, 
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  ListItemIcon,
} from "@mui/material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import ClosedCaptionIcon from "@mui/icons-material/ClosedCaption";
import ClosedCaptionOffIcon from "@mui/icons-material/ClosedCaptionDisabled";
import Tooltip from "@mui/material/Tooltip";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Room, RoomEvent, Track, createLocalAudioTrack, DataPacket_Kind } from "livekit-client";
import logo from "./assets/tom-logo.png";
import tomIcon from "./assets/tom-icon-c.png";
import { z } from 'zod';

const EmailSchema = z.string().email().max(255);
const RoomNameSchema = z.string().min(1).max(100).regex(/^[a-zA-Z0-9_-]+$/);
const CodeSchema = z.string().min(10).max(2000).regex(/^[A-Za-z0-9/_-]+$/);

const apiCallTracker = new Map();
const MAX_CALLS_PER_MINUTE = 10;

function base64UrlDecode(input) {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
  return decodeURIComponent(
    Array.prototype.map.call(
      atob(b64 + pad),
      c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join('')
  );
}

function validateJwtToken(token) {
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    const payload = JSON.parse(base64UrlDecode(parts[1])); // FIXED: Use base64UrlDecode
    
    if (payload.exp * 1000 < Date.now()) return false;
    if (!payload.iss || !payload.iss.includes('accounts.google.com')) return false;
    if (payload.aud !== GOOGLE_CLIENT_ID) return false;
    
    return true;
  } catch {
    return false;
  }
}

function sanitizeInput(input) {
  if (typeof input !== 'string') return input;
  return input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
              .replace(/javascript:/gi, '')
              .replace(/on\w+\s*=/gi, '');
}

function checkRateLimit(endpoint) {
  const now = Date.now();
  const key = endpoint.split('/').pop(); // Use endpoint name as key
  const calls = apiCallTracker.get(key) || [];
  const recentCalls = calls.filter(time => now - time < 60000);
  
  if (recentCalls.length >= MAX_CALLS_PER_MINUTE) {
    throw new Error("Too many requests. Please wait a moment.");
  }
  
  recentCalls.push(now);
  apiCallTracker.set(key, recentCalls);
}

/* ========================== THEME ========================== */
const theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#f97316" },
    background: { default: "#0e1117", paper: "#181b21" },
    text: { primary: "#fff", secondary: "#b2bcd6" },
  },
  shape: { borderRadius: 14 },
  typography: {
    fontFamily: `"Montserrat","Inter","Roboto",sans-serif`,
    button: { fontWeight: 700, textTransform: "none" },
  },
});

const ripple = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(123,183,255,.34) }
  70%  { box-shadow: 0 0 0 24px rgba(123,183,255,0) }
  100% { box-shadow: 0 0 0 0 rgba(123,183,255,0) }
`;

/* ===================== ENV / CONSTANTS ===================== */
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const BACKEND_API_CR = process.env.REACT_APP_BACKEND_API_CR; // Cloud Run base URL
const BACKEND_API = process.env.REACT_APP_BACKEND_API; // Cloud Functions base URL

// Add validation
if (!GOOGLE_CLIENT_ID || !BACKEND_API_CR || !BACKEND_API) {
  throw new Error("Missing required environment variables");
}

// CASA-compliant endpoint configuration
const ENDPOINTS = {
  // OAuth handled by Cloud Functions
  oauth: `${BACKEND_API}/oauth2callbackFn`,
  
  // Call tokens handled by Cloud Functions  
  callToken: `${BACKEND_API}/callTokenFn`,
  
  // Session management handled by Cloud Run (choose one backend consistently)
  //logout: `${BACKEND_API_CR}/auth/logout`,
  //validate: `${BACKEND_API_CR}/auth/validate`,
  
  // Alternative: Use Cloud Functions for session management
   logout: `${BACKEND_API}/logoutFn`,
   validate: `${BACKEND_API}/sessionValidateFn`,
  
  // Agent management handled by Cloud Run
  startAgent: `${BACKEND_API_CR}/start-agent`,
  stopAgent: `${BACKEND_API_CR}/stop-agent`,
};

const REDIRECT_URI = window.location.origin + "/oauth2callbackFn";
const OAUTH_SCOPES = [
  "openid",
  "email",
  "profile",
  "https://www.googleapis.com/auth/gmail.readonly",
  "https://www.googleapis.com/auth/gmail.send",
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/userinfo.profile",
  "https://www.googleapis.com/auth/gmail.modify",
  "https://www.googleapis.com/auth/gmail.compose",
  "https://www.googleapis.com/auth/gmail.labels",
].join(" ");

const ALLOWED_ORIGINS = [
  'https://app.hitom.ai',
  'https://demo.hitom.ai', 
  'https://hitom.ai',
  'http://localhost:3000' // Allow for development
];

/* ======================= COMPONENT ========================= */
export default function App() {

  // Gate state (so Hooks are always called in all renders)
  const [blockReason, setBlockReason] = useState(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    if (!ALLOWED_ORIGINS.includes(window.location.origin)) {
      setBlockReason('origin');
      return;
    }
    if (window.location.protocol !== 'https:') {
      setBlockReason('https');
      // Redirect after first render; keep Hooks ordering intact
      window.location.replace(window.location.href.replace('http:', 'https:'));
    }
  }, []);

  // UI flow: loading | login | ready | active
  const [stage, setStage] = useState("loading");
  const [loadingMsg, setLoadingMsg] = useState("Loading…");
  const [announcement, setAnnouncement] = useState("");

  // OAuth state
  const [tokens, setTokens] = useState(null);
  const [user, setUser] = useState(null);
  
  // Add flag to track OAuth callback flow - CRITICAL FOR iOS
  const [isAuthCallback, setIsAuthCallback] = useState(false);

  // Call / labels
  const [connected, setConnected] = useState(false);
  const [subtitle, setSubtitle] = useState("");
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [thinking, setThinking] = useState(false);

  // A11y / controls
  const [isMuted, setIsMuted] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  // UI bits
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "info",
    msg: "",
  });

  // LiveKit refs
  const [agentSession, setAgentSession] = useState(null);
  const roomRef = useRef(null);
  const localMicRef = useRef(null);
  const mainButtonRef = useRef(null);

  const captionsOnRef = useRef(captionsOn);
  useEffect(() => { captionsOnRef.current = captionsOn; }, [captionsOn]);

  // Session timeout and validation
  useEffect(() => {
    if (isAuthCallback) return;
    
    if (tokens?.id_token && !validateJwtToken(tokens.id_token)) {
      console.log("JWT validation failed");
      showError("Session expired for security");
      handleLogout();
    }
  }, [tokens, isAuthCallback]);

   useEffect(() => {
    if (isAuthCallback) return; // Skip during OAuth callback
    
    let sessionTimeout;
    let warningTimeout;
    
    if (tokens) {
      warningTimeout = setTimeout(() => {
        setSnackbar({ 
          open: true, 
          severity: "warning", 
          msg: "Session expires in 5 minutes. Please save your work." 
        });
      }, 25 * 60 * 1000);

      sessionTimeout = setTimeout(() => {
        showError("Session expired for security");
        handleLogout();
      }, 30 * 60 * 1000);
    }
    
    return () => {
      if (sessionTimeout) clearTimeout(sessionTimeout);
      if (warningTimeout) clearTimeout(warningTimeout);
    };
  }, [tokens, isAuthCallback]); // Add isAuthCallback to dependencies

  /* ---------- polite announcements for screen readers ---------- */
  const announce = (message) => {
    setAnnouncement(message);
    setTimeout(() => setAnnouncement(""), 100);
  };

  useEffect(() => {
    if (speaking) announce("Assistant is now speaking");
    if (connected && !speaking) announce("Now listening for your voice");
    if (thinking) announce("Assistant is thinking");
  }, [speaking, connected, thinking]);

  // Respect OS reduced motion
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    setReducedMotion(mq.matches);
    const handler = (e) => setReducedMotion(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  /* ---------- OAuth callback parse ---------- */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const st = params.get("state");
    
    if (code && st === "login") {
      setIsAuthCallback(true); // Mark that we're in callback flow
      
      try {
        const validatedCode = CodeSchema.parse(sanitizeInput(code));
        try {
          checkRateLimit('oauth2callbackFn');
        } catch (rateLimitError) {
          setSnackbar({ open: true, severity: "error", msg: rateLimitError.message });
          setStage("login");
          setIsAuthCallback(false);
          return;
        }
        
        setLoadingMsg("Signing in…");
        fetch(ENDPOINTS.oauth, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: validatedCode, redirectUri: REDIRECT_URI }),
        })
        .then(async (r) => {
          const responseText = await r.text();
          console.log("OAuth response status:", r.status);
          console.log("OAuth response body:", responseText);
          
          if (!r.ok) {
            let errorMsg = "Authentication failed";
            try {
              const errorData = JSON.parse(responseText);
              errorMsg = errorData.error || errorMsg;
            } catch (e) {
              errorMsg = `Server error (${r.status})`;
            }
            throw new Error(errorMsg);
          }
          
          return JSON.parse(responseText);
        })
        .then((d) => {
          console.log("OAuth response data:", { 
            hasTokens: !!d.tokens, 
            hasProfile: !!d.profile,
            hasError: !!d.error 
          });
          
          if (d.error) throw new Error(d.error);
          if (!d.tokens || !d.profile) throw new Error("Incomplete authentication response");
          
          // Set tokens and user
          setTokens(d.tokens);
          setUser(d.profile);
          setStage("ready");
          
          // Clean up URL
          window.history.replaceState({}, document.title, "/");
          
          // Mark callback complete AFTER tokens are set
          setTimeout(() => setIsAuthCallback(false), 100);
        })
        .catch((error) => {
          console.error("Auth error details:", error);
          const userMessage = error.message || "Authentication failed. Please try again.";
          setSnackbar({ 
            open: true, 
            severity: "error", 
            msg: userMessage
          });
          setStage("login");
          setIsAuthCallback(false);
          window.history.replaceState({}, document.title, "/");
        });
      } catch (validationError) {
        setSnackbar({ open: true, severity: "error", msg: "Invalid authentication data" });
        setStage("login");
        setIsAuthCallback(false);
        window.history.replaceState({}, document.title, "/");
        return;
      }
    } else {
      setStage("login");
      setIsAuthCallback(false);
    }
  }, []);

  // Add session validation on app startup
   useEffect(() => {
    if (isAuthCallback) return;
    
    const validateSession = async () => {
      if (tokens?.id_token) {
        try {
          const response = await fetch(ENDPOINTS.validate, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokens.id_token}`,
            },
          });
          
          if (!response.ok) {
            console.log("Server session validation failed");
            handleLogout();
          }
        } catch (error) {
          console.log("Session validation error:", error);
          handleLogout();
        }
      }
    };
    
    if (tokens && !isAuthCallback) {
      validateSession();
    }
  }, [tokens, isAuthCallback]);

  // Add to App.js - detect logout in other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user_logged_out' && e.newValue === 'true') {
        // Another tab logged out - invalidate this session
        handleLogout();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  /* ========================== UI helpers ========================== */
  
  async function leaveCall() {
    try {
      const sid = agentSession?.sessionId;
      if (sid && tokens?.id_token) {
        try {
          await fetch(ENDPOINTS.stopAgent, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokens.id_token}`,
            },
            body: JSON.stringify({ userEmail: user.email, sessionId: sid }),
          });
        } catch {}
      }
    } finally {
      handleRoomDisconnected("Left the call");
    }
  }

  const showError = (msg) =>
    setSnackbar({ open: true, severity: "error", msg });

  const handleGoogleLogin = () => {
    setStage("loading");
    setLoadingMsg("Redirecting to Google…");
    const url =
      `https://accounts.google.com/o/oauth2/v2/auth` +
      `?client_id=${encodeURIComponent(GOOGLE_CLIENT_ID)}` +
      `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent(OAUTH_SCOPES)}` +
      `&access_type=offline` +
      `&prompt=consent` +
      `&state=login`;
    window.location.href = url;
  };

  const handleLogout = async () => {
    try {
      // CRITICAL: Call server-side logout first
      if (tokens?.id_token) {
        await fetch(ENDPOINTS.logout, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokens.id_token}`,
          },
        });
      }
    } catch (error) {
      console.warn("Server-side logout failed, proceeding with client cleanup");
    } finally {
      // Clear all client-side state
      leaveCall().finally(() => {
        setTokens(null);
        setUser(null);
        setStage("login");

        // Notify other tabs
        localStorage.setItem('user_logged_out', 'true');
        localStorage.removeItem('user_logged_out');
        
        // Clear any cached data
        localStorage.clear();
        sessionStorage.clear();
        
        // Close menu
        setMenuAnchor(null);
        
        // Force page reload to clear any cached state
        window.location.reload();
      });
    }
  };

  const bigLabel = () => {
    if (stage === "loading") return loadingMsg || "Loading…";
    if (stage === "login") return "Login with Google";
    if (stage === "ready") return "Start";
    if (stage === "active")
      return connected ? (speaking ? "" : "Connected") : "Start";
    return "";
  };

  const handleBigBtn = () => {
    if (stage === "login") return handleGoogleLogin();
    if (stage === "ready") return joinCall();
    if (stage === "active") {
      if (connected) return leaveCall();
      return joinCall();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      handleBigBtn();
    }
    if (e.key === "Escape") {
      setMenuAnchor(null);
      if (connected) leaveCall();
    }
  };

  useEffect(() => {
    if (mainButtonRef.current && (stage === "ready" || stage === "active")) {
      mainButtonRef.current.focus();
    }
  }, [stage]);

  const getAriaLabel = () => {
    if (stage === "loading") return "Loading, please wait";
    if (stage === "login")
      return "Login with Google to start voice conversation";
    if (stage === "ready") return "Join a voice call with your assistant";
    if (stage === "active") return connected ? "Leave the call" : "Join the call";
    return "Voice assistant control";
  };

  async function startAgent(roomName) {
    const res = await fetch(ENDPOINTS.startAgent, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tokens.id_token}`,
      },
      body: JSON.stringify({
        userEmail: user.email,
        roomName,
        language: (navigator.language || "en").split("-")[0],
      }),
    });
    if (!res.ok) throw new Error(`startAgentFn ${res.status}`);
    const data = await res.json();
    setAgentSession(data.agent || null);
    return data;
  }

  function sendAgentConfig() {
    const room = roomRef.current;
    if (!room || !tokens?.id_token || !user?.email) return;

    const payload = {
      type: "config",
      version: 1,
      email: user.email,
      language: navigator.language || "en",
      captions: captionsOnRef.current,
    };

    const data = new TextEncoder().encode(JSON.stringify(payload));
    room.localParticipant.publishData(data, DataPacket_Kind.RELIABLE).catch(() => {});
  }

  /* =================== Global keyboard shortcuts =================== */
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag)) return;

      // Shift+? (or ?) → Help
      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }
      // J → Join/Leave
      if (e.key.toLowerCase() === "j") {
        e.preventDefault();
        handleBigBtn();
        return;
      }
      // M → Mute/unmute
      if (e.key.toLowerCase() === "m") {
        e.preventDefault();
        toggleMute();
        return;
      }
      // C → Captions on/off
      if (e.key.toLowerCase() === "c") {
        e.preventDefault();
        toggleCaptions();
        return;
      }
      // Esc → close help or menu
      if (e.key === "Escape") {
        if (helpOpen) setHelpOpen(false);
        if (menuAnchor) setMenuAnchor(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [helpOpen, connected, isMuted, captionsOn, stage, menuAnchor]);

  /* ======================== Call handling (LiveKit) ======================== */
  const onDataReceived = (payload) => {
    try {
      const msg = JSON.parse(new TextDecoder().decode(payload));

      if (msg?.type === "agent_subtitle" && typeof msg.text === "string") {
        if (captionsOnRef.current) setSubtitle(msg.text);
      } else if (msg?.type === "agent_status") {
        setThinking(Boolean(msg.thinking));
      } else if (msg?.type === "agent_ready") {
        // agent says its data channel is ready → (re)send config
        sendAgentConfig();
      }
    } catch {
      /* ignore malformed packets */
    }
  };

  function handleRoomDisconnected(reason = "Call disconnected") {
    try { roomRef.current?.disconnect(); } catch {}
    try { localMicRef.current?.stop(); } catch {}

    roomRef.current = null;
    localMicRef.current = null;
    setAgentSession(null);
    setConnected(false);
    setListening(false);
    setThinking(false);
    setSpeaking(false);
    setSubtitle("");
    setIsMuted(false);
    setStage(tokens ? "ready" : "login");
    setSnackbar({ open: true, severity: "warning", msg: reason });
  }

  async function ensureAudioContext() {
  try {
    // Resume audio context if suspended
    if (window.AudioContext || window.webkitAudioContext) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioContext();
      
      if (audioCtx.state === 'suspended') {
        await audioCtx.resume();
      }
      audioCtx.close();
    }
  } catch (error) {
    console.warn('Audio context setup failed:', error);
  }
}

  async function joinCall() {
  if (!tokens?.id_token) return showError("Please sign in first.");
  try {
    setStage("loading");
    try {
      checkRateLimit('callTokenFn');
    } catch (rateLimitError) {
      return showError(rateLimitError.message);
    }
    setLoadingMsg("Preparing call…");

    let validatedEmail;
    try {
      validatedEmail = EmailSchema.parse(user?.email);
    } catch (validationError) {
      return showError("Invalid user data");
    }

    const res = await fetch(ENDPOINTS.callToken, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tokens.id_token}`,
      },
      body: JSON.stringify({
        userEmail: validatedEmail,
      }),
    });
    if (!res.ok) throw new Error(`callTokenFn ${res.status}`);
    const { url, token, roomName } = await res.json();

    // REMOVED: ensureAudioContext() call - this was potentially interfering
    
    // FIXED: Minimal Room configuration - let LiveKit handle audio processing
    const room = new Room({
      // REMOVED: Excessive audio configuration that could conflict with realtime models
      // Let LiveKit use its default settings for realtime voice models
    });
    roomRef.current = room;

    // Events - FIXED: Simplified audio track handling
    room.on(RoomEvent.ParticipantConnected, (p) => {
      if ((p.identity || "").startsWith("agent-")) {
        setTimeout(sendAgentConfig, 300);
      }
    })
    .on(RoomEvent.ParticipantDisconnected, () =>
      console.log("[LK] Participant disconnected")
    )
    .on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind === Track.Kind.Audio) {
        const el = track.attach();
        el.setAttribute("playsinline", "");
        el.setAttribute("aria-hidden", "true");
        el.autoplay = true;
        // FIXED: Simplified audio element configuration
        el.volume = 1.0;
        // REMOVED: Excessive audio element configuration that could cause playback issues
        document.body.appendChild(el);
        setSpeaking(true);
        el.onended = () => setSpeaking(false);

        // REMOVED: Complex error handling that could interfere with playback
      }
    })
    .on(RoomEvent.TrackUnsubscribed, (track) => {
      if (track.kind === Track.Kind.Audio) {
        try {
          track.detach().forEach((el) => el.remove());
        } catch {}
        setSpeaking(false);
      }
    })
    .on(RoomEvent.ConnectionStateChanged, (state) => {
      console.log("[LK] Connection state changed");
      const s = String(state || room.state).toLowerCase();
      if (s.includes("disconnected")) handleRoomDisconnected();
    })
    .on(RoomEvent.Disconnected, (reason) => {
      handleRoomDisconnected(reason || "Call disconnected");
    });

    await room.connect(url, token);

    // FIXED: Simplified microphone configuration
    const mic = await createLocalAudioTrack({ 
      echoCancellation: true, 
      noiseSuppression: true, 
      autoGainControl: true,
      // REMOVED: Excessive audio configuration that could interfere with realtime models
      // Let the browser and LiveKit use default settings
    });
    localMicRef.current = mic;
    await room.localParticipant.publishTrack(mic);

    room.on(RoomEvent.DataReceived, onDataReceived);

    // Start agent
    try {
      await startAgent(roomName);
      sendAgentConfig();
      setTimeout(sendAgentConfig, 1000);
    } catch (e) {
      console.error("startAgent failed:", e);
      setSnackbar({ open: true, severity: "warning", msg: "Assistant join delayed. Retrying…" });
      setTimeout(async () => {
        try { 
          await startAgent(roomName);
          sendAgentConfig(); 
        } catch {}
      }, 1200);
    }

    setConnected(true);
    setListening(true);
    setThinking(false);
    setStage("active");
    setSnackbar({ open: true, severity: "success", msg: "Connected" });
  } catch (err) {
    console.error("joinCall error occurred");
    setConnected(false);
    setListening(false);
    setThinking(false);
    setStage("ready");
    showError("Unable to connect. Please check your connection and try again.");
  }
}

  async function toggleMute() {
    const room = roomRef.current;
    if (!room) return;
    const next = !isMuted;
    try {
      await room.localParticipant.setMicrophoneEnabled(!next);
      setIsMuted(next);
      announce(next ? "Microphone muted" : "Microphone unmuted");
    } catch {}
  }

  function toggleCaptions() {
    const next = !captionsOn;
    setCaptionsOn(next);
    announce(next ? "Captions turned on" : "Captions turned off");
    // let the agent know
    try { sendAgentConfig(); } catch {}
  }

  // Blocked UI paths must be handled here so Hooks above still run every render
  if (blockReason === 'origin') {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Access Denied</h1>
      </div>
    );
  }

  if (blockReason === 'https') {
    // Show nothing (or a tiny message) while the browser redirects.
    return null;
  }
  
  /* ============================ UI ============================ */
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* Skip link & live region */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>

      {/* Top App Bar */}
      <AppBar
        position="static"
        color="transparent"
        elevation={0}
        sx={{ backdropFilter: "blur(6px)" }}
      >
        <Toolbar>
          <IconButton
            id="menu-button"
            edge="start"
            color="inherit"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-label="Open user menu"
            aria-controls={Boolean(menuAnchor) ? "user-menu" : undefined}
            aria-expanded={Boolean(menuAnchor)}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          {user && (
            <Avatar
              alt={user.name}
              src={user.picture || logo}
              sx={{ width: 36, height: 36 }}
            />
          )}
        </Toolbar>
      </AppBar>

      <Menu
        id="user-menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        aria-labelledby="menu-button"
        MenuListProps={{ role: "menu", "aria-labelledby": "menu-button" }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {user && (
          <MenuItem disabled role="menuitem" sx={{ opacity: 1, cursor: 'default' }}>
            <ListItemIcon>
              <Avatar
                src={user.picture || logo}
                sx={{ width: 24, height: 24 }}
              />
            </ListItemIcon>
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {user.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
          </MenuItem>
        )}
        
        <Divider />
        
        <MenuItem 
          role="menuitem"
          onClick={() => {
            setMenuAnchor(null);
            setHelpOpen(true);
          }}
        >
          <ListItemIcon>
            <HelpOutlineIcon fontSize="small" />
          </ListItemIcon>
          Help & Shortcuts
        </MenuItem>
        
        <MenuItem 
          role="menuitem"
          onClick={() => {
            setMenuAnchor(null);
            // TODO: Implement settings
          }}
          disabled
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings (soon)
        </MenuItem>
        
        <Divider />
        
        {tokens && (
          <MenuItem
            role="menuitem"
            onClick={handleLogout}
            aria-label="Logout from your account"
            sx={{ 
              color: 'error.main',
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.04)
              }
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            Logout
          </MenuItem>
        )}
      </Menu>

      {/* Main – big round button */}
      <Container
        component="main"
        aria-label="Voice assistant call interface"
        id="main-content"
        maxWidth="sm"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "78vh",
        }}
      >
        <h1 className="sr-only">TOM Voice Assistant</h1>

        <Button
          ref={mainButtonRef}
          onClick={handleBigBtn}
          onKeyDown={handleKeyDown}
          disabled={stage === "loading"}
          variant="contained"
          size="large"
          aria-label={getAriaLabel()}
          aria-pressed={connected ? "true" : "false"}
          aria-describedby="status-text"
          sx={{
            width: 180,
            height: 180,
            borderRadius: "50%",
            bgcolor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: stage === "loading" ? "default" : "pointer",
            animation:
              speaking && !reducedMotion ? `${ripple} 1.4s infinite` : "none",
            transition: "transform 0.14s",
            boxShadow: speaking
              ? `0 0 0 4px ${alpha("#7bb7ff", 0.4)}`
              : "0 6px 18px rgba(0,0,0,.45)",
            "&:active": {
              transform: stage === "loading" ? "none" : "scale(.94)",
            },
            "&:focus-visible": {
              outline: "3px solid #fff",
              outlineOffset: "4px",
            },
            border: "none",
            padding: 0,
            minWidth: "unset",
          }}
        >
          {stage === "loading" ? (
            <CircularProgress size={66} color="inherit" aria-label="Loading" />
          ) : (
            <img
              src={tomIcon}
              alt="TOM Voice Assistant"
              draggable={false}
              style={{ width: 180, height: 180, borderRadius: "50%" }}
            />
          )}
        </Button>

        {/* Status / captions */}
        <Typography
          id="status-text"
          mt={4}
          variant="h6"
          color="text.primary"
          textAlign="center"
          sx={{ minHeight: 28, px: 2 }}
          aria-live="polite"
          role="status"
        >
          {subtitle || bigLabel()}
        </Typography>

        {/* Control bar: Mute / Captions / Voice Commands */}
{connected && (
  <Box
    role="group"
    aria-label="Call controls"
    sx={{ mt: 3, display: "flex", gap: 2, alignItems: "center", justifyContent: "center" }}
  >
    {/* Mute / Unmute */}
    <Tooltip title={isMuted ? "Unmute (M)" : "Mute (M)"} arrow>
      <Button
        variant="outlined"
        onClick={toggleMute}
        aria-pressed={isMuted ? "true" : "false"}
        aria-label={isMuted ? "Unmute microphone" : "Mute microphone"}
        sx={{
          minWidth: 48,
          p: 1.25,
          color: 'text.primary',
          borderColor: 'grey.700',
          '&:hover': {
            borderColor: 'grey.600',
            backgroundColor: alpha(theme.palette.primary.main, 0.04)
          }
        }}
      >
        {isMuted ? <MicOffIcon /> : <MicIcon />}
      </Button>
    </Tooltip>

    {/* Captions toggle (optional) *}
    <Tooltip title={captionsOn ? "Turn captions off (C)" : "Turn captions on (C)"} arrow>
      <Button
        variant="outlined"
        onClick={toggleCaptions}
        aria-pressed={captionsOn ? "true" : "false"}
        aria-label={captionsOn ? "Disable captions" : "Enable captions"}
        sx={{
          minWidth: 48,
          p: 1.25,
          color: 'text.primary',
          borderColor: 'grey.700',
          '&:hover': {
            borderColor: 'grey.600',
            backgroundColor: alpha(theme.palette.primary.main, 0.04)
          }
        }}
      >
        {captionsOn ? <ClosedCaptionIcon /> : <ClosedCaptionOffIcon />}
      </Button>
    </Tooltip>
    */}

    {/* Voice commands help */}
    <Button
      variant="outlined"
      onClick={() => setHelpOpen(true)}
      aria-label="Show available voice commands"
      sx={{
        minWidth: 160,
        color: 'text.primary',
        borderColor: 'grey.700',
        '&:hover': {
          borderColor: 'grey.600',
          backgroundColor: alpha(theme.palette.primary.main, 0.04)
        }
      }}
    >
      Voice Commands
    </Button>
  </Box>
)}


        {/* Signed-in identity hint */}
        {user && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            {user.name} · {user.email}
          </Typography>
        )}
      </Container>

      {/* Help & Shortcuts dialog */}
      <Dialog
  open={helpOpen}
  onClose={() => setHelpOpen(false)}
  aria-labelledby="help-title"
  aria-describedby="help-desc"
  maxWidth="sm"
  fullWidth
>
  <DialogTitle id="help-title">
    Voice Commands & Help
  </DialogTitle>
  <DialogContent id="help-desc">
    <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
      Email Commands
    </Typography>
    <Box component="ul" sx={{ pl: 2, mb: 3 }}>
      <li><Typography>Say <strong>"start reading"</strong> to begin</Typography></li>
      <li><Typography><strong>"next"</strong> - Move to next email</Typography></li>
      <li><Typography><strong>"previous"</strong> - Go back to previous email</Typography></li>
      <li><Typography><strong>"archive"</strong> - Archive current email</Typography></li>
      <li><Typography><strong>"star"</strong> - Star current email</Typography></li>
      <li><Typography><strong>"reply [message]"</strong> - Draft a reply</Typography></li>
      <li><Typography><strong>"read full email"</strong> - Get complete content</Typography></li>
      <li><Typography><strong>"attachments"</strong> - List file attachments</Typography></li>
    </Box>

    <Typography variant="h6" gutterBottom>
      Reply Workflow
    </Typography>
    <Box component="ul" sx={{ pl: 2, mb: 3 }}>
      <li><Typography>Draft: <strong>"reply [your message]"</strong></Typography></li>
      <li><Typography>Edit: Say <strong>"edit"</strong> then new content</Typography></li>
      <li><Typography>Send: Say <strong>"yes send it"</strong></Typography></li>
      <li><Typography>Save: Say <strong>"no"</strong> to save as draft</Typography></li>
    </Box>

    <Typography variant="h6" gutterBottom>
      Keyboard Shortcuts
    </Typography>
    <Box component="ul" sx={{ pl: 2, mb: 3 }}>
  <li>
    <Typography component="span">
      <kbd style={{
        backgroundColor: theme.palette.grey[800],
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '0.85em',
        fontFamily: 'monospace'
      }}>J</kbd> — Join/Leave call
    </Typography>
  </li>
  <li>
    <Typography component="span">
      <kbd style={{
        backgroundColor: theme.palette.grey[800],
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '0.85em',
        fontFamily: 'monospace'
      }}>M</kbd> — Mute/Unmute microphone
    </Typography>
  </li>
  <li>
    <Typography component="span">
      <kbd style={{
        backgroundColor: theme.palette.grey[800],
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '0.85em',
        fontFamily: 'monospace'
      }}>C</kbd> — Toggle captions
    </Typography>
  </li>
  <li>
    <Typography component="span">
      <kbd style={{
        backgroundColor: theme.palette.grey[800],
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '0.85em',
        fontFamily: 'monospace'
      }}>Esc</kbd> — Close dialogs or menu
    </Typography>
  </li>
</Box>
    
    <Typography variant="h6" gutterBottom>
      How to Use
    </Typography>
    <Typography paragraph>
      Speak naturally to your voice assistant. Start with "start reading" to begin going through your emails. 
      The assistant will read each email and wait for your commands.
    </Typography>
    
    <Typography variant="h6" gutterBottom>
      Privacy & Security
    </Typography>
    <Typography paragraph color="text.secondary">
      Your conversations are processed in real-time and not permanently stored. 
      You can logout anytime from the menu.
    </Typography>

    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
      <Button onClick={() => setHelpOpen(false)} variant="contained">
        Close
      </Button>
    </Box>
  </DialogContent>
</Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        aria-live="assertive"
        role="alert"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          aria-describedby="alert-message"
          sx={{ minWidth: 300 }}
        >
          <span id="alert-message">{snackbar.msg}</span>
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}